
/** A crossing this tab is in the middle of. */
export interface PendingCrossing {
  /** The destination map's short key -- `a3`, not `metin2_map_a3`. */
  readonly map: string;
  /** Opaque. Presented once, in `Arrive`, and never read. */
  readonly token: string;
}

export const CROSSING_KEY = "metin3.crossing";

export function mapKeyOf(mapId: string): string {
  const normalized = mapId.toLowerCase();
  const prefix = "metin2_map_";
  return normalized.startsWith(prefix) ? normalized.slice(prefix.length) : normalized;
}

export function sessionStore(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Write down a crossing this tab is about to make. Returns whether it stuck --
 *  a caller that gets `false` should not reload, because the page that came
 *  back would have nothing to identify itself with. */
export function rememberCrossing(
  store: Storage | null,
  mapId: string,
  token: string,
): boolean {
  if (store === null || token === "") return false;
  const pending: PendingCrossing = { map: mapKeyOf(mapId), token };
  try {
    store.setItem(CROSSING_KEY, JSON.stringify(pending));
    return true;
  } catch {
    // Quota, or a browser that allows the accessor and refuses the write.
    return false;
  }
}

/** The crossing this page was opened by, if any -- and clear it. Reading is
 *  destructive on purpose; see the module docstring. */
export function takeCrossing(store: Storage | null): PendingCrossing | null {
  if (store === null) return null;
  let raw: string | null = null;
  try {
    raw = store.getItem(CROSSING_KEY);
    store.removeItem(CROSSING_KEY);
  } catch {
    return null;
  }
  if (raw === null || raw === "") return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingCrossing>;
    if (typeof parsed.map !== "string" || typeof parsed.token !== "string") {
      return null;
    }
    if (parsed.map === "" || parsed.token === "") return null;
    return { map: parsed.map, token: parsed.token };
  } catch {
    // Somebody else's key, or a half-written value. Not worth a boot failure.
    return null;
  }
}

export const CROSSING_MARKS_KEY = "metin3.crossmarks";

export function markCrossing(step: string, detail: unknown = null): void {
  try {
    const store = globalThis.localStorage ?? null;
    if (store === null) return;
    const raw = store.getItem(CROSSING_MARKS_KEY);
    const marks = raw === null ? [] : (JSON.parse(raw) as unknown[]);
    marks.push({ step, detail, at: Math.round(performance.now()) });
    while (marks.length > 64) marks.shift();
    store.setItem(CROSSING_MARKS_KEY, JSON.stringify(marks));
  } catch {
    // Instrumentation never breaks a boot.
  }
}

/** One crossing order held for a sink that registers after it arrived. A login
 *  that lands off the front-door map is told to cross in the frame after its
 *  Welcome (`GameLoop.load_own_map`), before the world screen has subscribed;
 *  the order is delivered on subscription instead of being dropped. */
export class HeldCrossing<T> {
  private sink: ((change: T) => void) | null = null;
  private held: T | null = null;

  deliver(change: T): void {
    if (this.sink === null) this.held = change;
    else this.sink(change);
  }

  subscribe(sink: (change: T) => void): void {
    this.sink = sink;
    const held = this.held;
    this.held = null;
    if (held !== null) sink(held);
  }
}
